import React, { useState } from 'react';
import { ArrowPathIcon, Cog6ToothIcon } from '../../icons';
import { transcriptionAPI } from '../../services/api';
import MatDashPanel from '../layout/MatDashPanel';
import JobStatusBadge from './JobStatusBadge';
import {
  getEtaPlaceholder,
  normalizeJobStatus,
} from '../../utils/transcriptionStatus';
import { DISPLAY_ASR_MODEL, formatDateTime } from '../../utils/transcriptionMeta';
import { getLanguageLabel } from '../../utils/languages';

const JobMetadataPanel = ({
  transcription,
  onRetrySuccess,
  onStatusChange,
}) => {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');

  if (!transcription) return null;

  const jobStatus = normalizeJobStatus(transcription.jobStatus || transcription.status);
  const eta = getEtaPlaceholder(
    transcription.status,
    transcription.queued_at,
    transcription.eta_seconds,
  );
  const languageLabel = getLanguageLabel(transcription.language);

  const handleRetry = async () => {
    if (!transcription.id) return;
    setRetrying(true);
    setRetryError('');
    try {
      await transcriptionAPI.startTranscription(transcription.id);
      onRetrySuccess?.();
      onStatusChange?.('processing');
    } catch (err) {
      setRetryError(
        err.response?.data?.message || err.message || 'Retry failed. Please try again.'
      );
    } finally {
      setRetrying(false);
    }
  };

  return (
    <MatDashPanel
      title="Transcription job"
      icon={Cog6ToothIcon}
      headerAction={
        <JobStatusBadge status={transcription.status || transcription.jobStatus} />
      }
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="text-matdash-muted">Language</dt>
          <dd className="font-medium text-matdash-dark mt-0.5">
            {languageLabel || '—'}
          </dd>
        </div>
        <div>
          <dt className="text-matdash-muted">ASR model</dt>
          <dd className="font-medium text-matdash-dark mt-0.5">
            {DISPLAY_ASR_MODEL}
          </dd>
        </div>
        <div>
          <dt className="text-matdash-muted">Queued</dt>
          <dd className="font-medium text-matdash-dark mt-0.5">
            {formatDateTime(transcription.queued_at)}
          </dd>
        </div>
        <div>
          <dt className="text-matdash-muted">Completed</dt>
          <dd className="font-medium text-matdash-dark mt-0.5">
            {jobStatus === 'complete'
              ? formatDateTime(transcription.completed_at)
              : '—'}
          </dd>
        </div>
        {transcription.source_type && (
          <div>
            <dt className="text-matdash-muted">Source</dt>
            <dd className="font-medium text-matdash-dark mt-0.5 capitalize">
              {transcription.source_type.replace(/_/g, ' ')}
            </dd>
          </div>
        )}
      </dl>

      {eta && (jobStatus === 'queued' || jobStatus === 'processing') && (
        <p className="mt-4 md-alert md-alert-warning">{eta}</p>
      )}

      {jobStatus === 'failed' && (
        <div className="mt-4 md-alert md-alert-danger">
          <p className="font-medium">Transcription failed</p>
          <p className="mt-1">
            {transcription.failure_reason ||
              'An error occurred during processing. You can retry the job.'}
          </p>
          {retryError && (
            <p className="mt-2 text-red-600">{retryError}</p>
          )}
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className="mt-3 md-btn md-btn-danger inline-flex items-center gap-2"
          >
            <ArrowPathIcon className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying…' : 'Retry transcription'}
          </button>
        </div>
      )}
    </MatDashPanel>
  );
};

export default JobMetadataPanel;
